import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Ventana } from './Ventana';
import { textoDeError } from './Dialogo';
import { dinero, entero, RESULTADO } from '../lib/graficas';

/**
 * Lo que hay DETRÁS de una barra (0.46.0): "que no sólo salga el número". Abre `GET analytics/lista?…` en la ventana de
 * detalle de la app y enseña a las personas (cada una con liga a su Ficha), sus citas o sus ventas. La cuenta de la lista
 * es la misma que la de la barra: el servidor filtra con la MISMA regla que la gráfica.
 */
export interface PedidoLista { titulo: ReactNode; subtitulo?: ReactNode; qs: Record<string, string> }
interface Fila { patientId: string | null; nombre: string | null; fecha?: string; resultado?: string; clave?: string; canal?: string; pidio?: string | null; asesora?: string | null; asesoria?: string; etapa?: string; visitas?: number; pago?: boolean; alta?: string; concepto?: string; monto?: number; fuente?: string }
interface Resp { total: number | null; recortado: boolean; monto?: number; filas: Fila[] }

const ETAPA: Record<string, string> = { LEAD: 'Lead', PV: 'Primera visita', SUB: 'Recompra', UGC: 'UGC' };
const ESTADO: Record<string, string> = { programada: 'Programada', confirmada: 'Confirmada', completada: 'Llegó', cancelada: 'Canceló', no_show: 'No llegó', en_progreso: 'En curso' };
const FUENTE: Record<string, string> = { pos: 'recepción', tienda: 'tienda de la app', crm_excel: 'corte del CRM' };
const fecha = (d?: string) => (d ? new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--ink-soft)', fontWeight: 600, whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderTop: '1px solid var(--border)', verticalAlign: 'top' };

export function ListaDetalle({ pedido, onClose }: { pedido: PedidoLista; onClose: () => void }) {
  const [d, setD] = useState<Resp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const tipo = pedido.qs.tipo;
  useEffect(() => {
    setD(null); setErr(null);
    api<Resp>('analytics/lista?' + new URLSearchParams(pedido.qs).toString()).then(setD).catch((e) => setErr(textoDeError(e)));
  }, [JSON.stringify(pedido.qs)]);
  const persona = (f: Fila) => (f.patientId
    ? <Link to={`/paciente/${f.patientId}`} style={{ color: 'var(--gold-dark)', fontWeight: 600 }}>{f.nombre || 'Sin nombre'}</Link>
    : <span className="muted">sin expediente</span>);
  const colorRes = (f: Fila) => (f.clave === 'llego' || f.resultado === 'completada' ? RESULTADO.llego : f.clave === 'cancelo' || f.resultado === 'cancelada' ? RESULTADO.cancelo : 'var(--ink-soft)');
  return (
    <Ventana datos="lista" ancho={820} onClose={onClose} titulo={pedido.titulo}
      subtitulo={<>{pedido.subtitulo}{d ? <> {d.total != null ? `${entero(d.total)} en total` : 'Más de 300'}{d.monto != null ? ` · ${dinero(d.monto)}` : ''}{d.recortado ? ' · se enseñan las 300 más recientes' : ''}.</> : null}</>}>
      {err && <div style={{ padding: 12, color: 'var(--warning)', background: 'var(--warning-bg)', borderRadius: 10 }}>{err}</div>}
      {!d && !err && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando…</div>}
      {d && d.filas.length === 0 && <p className="muted" style={{ fontSize: 13 }}>No hay nada que enseñar aquí.</p>}
      {d && d.filas.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }} data-lista-tabla={tipo}>
            <thead><tr>
              <th style={th}>Persona</th>
              {(tipo === 'citas' || tipo === 'agenda') && <><th style={th}>Cita</th><th style={th}>Resultado</th><th style={th}>Canal</th><th style={th}>Pidió</th></>}
              {tipo === 'embudo' && <><th style={th}>Llegó</th><th style={th}>Canal</th><th style={th}>Asesoría</th><th style={th}>Etapa</th></>}
              {tipo === 'ventas' && <><th style={th}>Fecha</th><th style={th}>Qué</th><th style={{ ...th, textAlign: 'right' }}>Monto</th></>}
            </tr></thead>
            <tbody>
              {d.filas.map((f, i) => (
                <tr key={i} data-lista-fila>
                  <td style={td}>{persona(f)}</td>
                  {(tipo === 'citas' || tipo === 'agenda') && <>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(f.fecha)}</td>
                    <td style={{ ...td, color: colorRes(f), fontWeight: 600, whiteSpace: 'nowrap' }}>{ESTADO[f.resultado || ''] || f.resultado}</td>
                    <td style={td} className="muted">{f.canal}</td>
                    <td style={{ ...td, wordBreak: 'break-word' }} className="muted">{f.pidio || '—'}</td>
                  </>}
                  {tipo === 'embudo' && <>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(f.alta)}</td>
                    <td style={td} className="muted">{f.canal}</td>
                    <td style={td} className="muted">{f.asesoria}</td>
                    <td style={td}>{ETAPA[f.etapa || ''] || f.etapa}{f.visitas ? <span className="muted"> · {f.visitas} {f.visitas === 1 ? 'visita' : 'visitas'}</span> : null}{f.pago ? <span className="muted"> · pagó</span> : null}</td>
                  </>}
                  {tipo === 'ventas' && <>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(f.fecha)}</td>
                    <td style={{ ...td, wordBreak: 'break-word' }}>{f.concepto}{f.fuente ? <span className="muted"> · {FUENTE[f.fuente] || f.fuente}</span> : null}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{dinero(f.monto || 0)}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Ventana>
  );
}
